import React, { useEffect, useState } from 'react'
import Select from 'react-select'
import find from 'lodash/find'
import { Formik } from 'formik'
import isEmpty from 'lodash/isEmpty'
import { Checkbox, ListItemText, MenuItem, OutlinedInput } from '@mui/material'
import MuiSelect from '@mui/material/Select'
import Tooltip from '@mui/material/Tooltip'
import axios from 'axios'
import { validationSchema } from './validationSchema'
import { t } from '../../i18n'
import KitchenConfiguration from './KitchenConfiguration'
import OrderProviderLocationForm from './OrderProviderLocationForm'
import moment from 'moment-timezone'

const scope = 'locations'

const LocationForm = (props) => {

  const fetchOrderProviderLocationFields = () => {
    axios.get('/admin/order_provider_locations/required_fields')
    .then((resp) => {
      setOrderProviderLocationFields(resp.data)
    })
  }

  const [orderProviderLocationFields, setOrderProviderLocationFields] = useState([])
  const [selectedOrderProviders, setSelectedOrderProviders] = useState([])
  const [orderProvidersSubmitted, setOrderProvidersSubmitted] = useState([])
  const [attachment, setAttachment] = useState(null)
  const [secureFieldsVisibility, setSecureFieldsVisibility] = useState({})

  useEffect(() => {
    fetchOrderProviderLocationFields()
    setSelectedOrderProviders(props.location.order_provider_locations || [])

    const initialOrderProvidersSubmitted = props.orderProviderOptions.map((provider) => ({
      provider_type: provider.provider_type,
      isSubmitted: props.location.order_provider_locations?.some((p) => p.provider_type === provider.provider_type) || false,
    }))

    setOrderProvidersSubmitted(initialOrderProvidersSubmitted)
  }, [])

  const setOrderProviderSubmitted = (provider_type, isSubmitted) => {
    const updatedOrderProvidersSubmitted = orderProvidersSubmitted.map((provider) => {
      if (provider.provider_type === provider_type) {
        return { ...provider, isSubmitted }
      }
      return provider
    })

    setOrderProvidersSubmitted(updatedOrderProvidersSubmitted)
  }

  const allOrderProvidersSubmitted = () => {
    return orderProvidersSubmitted.filter(
      (providerSubmitted) => selectedOrderProviders.some((provider) => provider.provider_type === providerSubmitted.provider_type)
    ).every((provider) => provider.isSubmitted)
  }

  return (
    <Formik
      initialValues={{
        ...props.location
      }}
      validationSchema={validationSchema}
      onSubmit={(values, { setStatus, setSubmitting }) => {
        const payload = {
          ...values,
          order_provider_locations_attributes: selectedOrderProviders,
        }
        return (
        props.onSubmit(payload, (success, errorData) => {
          let status = {}
          if (!success) {
            if(errorData.full_errors) {
              const fields = Object.getOwnPropertyNames(errorData.full_errors)
              for (let key in fields) {
                status[`${fields[key]}_error`] =
                  errorData.full_errors[fields[key]].join(', ')
              }
            }
          }
          setStatus(status)
          setSubmitting(false)
        }))
      }}
    >
      {({
        values,
        errors,
        touched,
        status,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        setStatus,
        setFieldValue
      }) => {

        const configErrorsPresent = () => {
          Object.values(props.kitchenConfigurationErrors).some(
            (error) => error
          )
        }

        const enhancedHandleChange = (e) => {
          let newStatus = Object.assign({}, status) // could be stale?
          newStatus[`${e.target.name}_error`] = ''
          setStatus(newStatus)
          handleChange(e)
        }

        const handleImageChange = (e) => {
          const file = e.target.files[0]
          if(file) {
            setAttachment(file)
            setFieldValue('attachment', file)
          }
        }

        const renderField = (
          label,
          name,
          type,
          placeHolder,
          autoComplete = 'off',
          isSecure = false
        ) => {
          const errorsPresent =
            (errors[name] && touched[name]) || (status && status[`${name}_error`])
          return (
            <div className="form-group">
              <label>{label}</label>
              <input
                id={name}
                name={name}
                type={type}
                onBlur={handleBlur}
                value={values[name] === null ? '' : values[name]}
                placeholder={placeHolder}
                autoComplete={autoComplete}
                onChange={enhancedHandleChange}
                className={`form-control inverted ${
                  errorsPresent && 'is-invalid'
                } ${isSecure ? 'masked-input' : ''}`}
              />
              {errorsPresent && (
                <div className="invalid-feedback">
                  {errors[name] || (status && status[`${name}_error`])}
                </div>
              )}
            </div>
          )
        }

        const renderTextAreaField = (
          label,
          name,
          placeHolder,
          autoComplete = 'off',
          isSecure = false
        ) => {
          const errorsPresent =
            (errors[name] && touched[name]) || (status && status[`${name}_error`])

          const getMaskedValue = (text) => {
            if (!text) return ''
            return text.split('').map(char => char === '\n' ? '\n' : '•').join('')
          }

          const fieldValue = values[name] === null ? '' : values[name]
          const isVisible = secureFieldsVisibility[name] || false

          const toggleVisibility = () => {
            setSecureFieldsVisibility(prev => ({
              ...prev,
              [name]: !prev[name]
            }))
          }

          if (isSecure) {
            return (
              <div className="form-group">
                <label>{label}</label>
                <div style={{ position: 'relative', width: '100%' }}>
                  <textarea
                    id={`${name}_hidden`}
                    name={name}
                    onBlur={handleBlur}
                    value={fieldValue}
                    placeholder={placeHolder}
                    autoComplete={autoComplete}
                    onChange={enhancedHandleChange}
                    style={{
                      position: 'absolute',
                      opacity: 0,
                      pointerEvents: 'none',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      resize: 'none'
                    }}
                    tabIndex={-1}
                  />

                  <textarea
                    id={`${name}_visible`}
                    value={isVisible ? fieldValue : getMaskedValue(fieldValue)}
                    readOnly
                    placeholder={placeHolder}
                    className={`form-control inverted ${
                      errorsPresent && 'is-invalid'
                    }`}
                    style={{
                      paddingRight: '60px',
                      fontFamily: "'Open Sans', sans-serif"
                    }}
                    onClick={() => document.getElementById(`${name}_hidden`).focus()}
                    onFocus={() => document.getElementById(`${name}_hidden`).focus()}
                  />

                  <button
                    type="button"
                    onClick={toggleVisibility}
                    className="btn btn-sm btn-secondary"
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      cursor: 'pointer',
                      zIndex: 10
                    }}
                  >
                    {isVisible ? 'Hide' : 'Show'}
                  </button>
                </div>
                {errorsPresent && (
                  <div className="invalid-feedback">
                    {errors[name] || (status && status[`${name}_error`])}
                  </div>
                )}
              </div>
            )
          }

          return (
            <div className="form-group">
              <label>{label}</label>
              <textarea
                id={name}
                name={name}
                onBlur={handleBlur}
                value={fieldValue}
                placeholder={placeHolder}
                autoComplete={autoComplete}
                onChange={enhancedHandleChange}
                className={`form-control inverted ${
                  errorsPresent && 'is-invalid'
                }`}
              />
              {errorsPresent && (
                <div className="invalid-feedback">
                  {errors[name] || (status && status[`${name}_error`])}
                </div>
              )}
            </div>
          )
        }

        const renderNameAndImageField = () => {
          const errorsPresent =
            (errors['name'] && touched['name']) || (status && status['name_error'])
          return (
            <div
              className="form-group location-name-image"
            >
              <div
                className="image-preview-container"
              >
                {renderImageField()}
              </div>
              <div
                className="name-input-container"
                style={{flex: '1', display: 'flex', flexDirection: 'column'}}
              >
                <label>Name</label>
                <input
                  id='name'
                  name='name'
                  type='text'
                  onBlur={handleBlur}
                  value={values['name'] === null ? '' : values['name']}
                  placeholder='Enter name'
                  autoComplete='off'
                  onChange={enhancedHandleChange}
                  className={`form-control inverted ${
                    errorsPresent && 'is-invalid'
                  }`}
                />
                {errorsPresent && (
                  <div className="invalid-feedback">
                    {errors['name'] || (status && status['name_error'])}
                  </div>
                )}
              </div>
            </div>
          )
        }

        const renderImageField = () => {
          const url = attachment ? URL.createObjectURL(attachment) : values['file_url']
          const errorPresent = (errors['attachment'] && touched['attachment']) || (status && status['attachment_error'])

          return (
            <div>
              <div className={`image-preview-wrapper ${errorPresent ? 'error-state' : ''}`}>
                <label htmlFor="attachment" className="image-label">
                  {!!url ? (
                    <img id="image" src={url} alt="Preview" className="image-preview" />
                  ) : (
                    <i className="bi bi-card-image placeholder-icon"></i>
                  )}
                  <div className="overlay">
                    <div className="overlay-text">
                      {!!url ? 'Change image' : 'Add image'}
                    </div>
                  </div>
                </label>
                <input
                  style={{ display: 'none' }}
                  id="attachment"
                  type="file"
                  accept='image/jpeg,image/png'
                  name="attachment"
                  onChange={handleImageChange}
                />
              </div>
              {errorPresent && (
                <div className="error-text">
                  {errors['attachment'] || (status && status['attachment_error'])}
                </div>
              )}
            </div>
          )
        }

        const renderRobotConnectionUrlField = () => {
          const errorsPresent =
            (errors['robot_connection_url'] && touched['robot_connection_url']) || (status && status['robot_connection_url_error'])
          return (
            <div className="form-group">
              <label>
                {t('robot_connection_url', { scope })}
                <Tooltip title={t('robot_connection_url_info', { scope })} placement='top' arrow>
                  <i className="fa fa-info-circle ms-1" />
                </Tooltip>
              </label>
              <input
                id='robot_connection_url'
                name='robot_connection_url'
                type='text'
                onBlur={handleBlur}
                value={values['robot_connection_url'] === null ? '' : values['robot_connection_url']}
                placeholder={t('enter_robot_connection_url', { scope })}
                autoComplete={'off'}
                onChange={enhancedHandleChange}
                className={`form-control inverted ${
                  errorsPresent && 'is-invalid'
                }`}
              />
              {errorsPresent && (
                <div className="invalid-feedback">
                  {errors['robot_connection_url'] || (status && status['robot_connection_url_error'])}
                </div>
              )}
            </div>
          )
        }

        const renderSensorEmailsField = () => {
          const [emailInput, setEmailInput] = useState('')
          const emails = values['sensor_report_emails'] || []
          const errorsPresent =
            (errors['sensor_report_emails'] && touched['sensor_report_emails']) ||
            (status && status['sensor_report_emails_error'])

          const isValidEmail = (email) => {
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
          }

          const addEmail = (e) => {
            if (e.key === 'Enter' || e.key === ',' || e.type === 'blur') {
              e.preventDefault()
              const email = emailInput.trim()

              if (email && isValidEmail(email) && !emails.includes(email)) {
                const updatedEmails = [...emails, email]
                setFieldValue('sensor_report_emails', updatedEmails)
                setEmailInput('')
              } else if (email && !isValidEmail(email)) {
                setStatus({
                  ...status,
                  sensor_report_emails_error: 'Please enter a valid email address'
                })
              }
            }
          }

          const removeEmail = (emailToRemove) => {
            const updatedEmails = emails.filter(email => email !== emailToRemove)
            setFieldValue('sensor_report_emails', updatedEmails)
          }

          const handleInputChange = (e) => {
            setEmailInput(e.target.value)
            if (status?.sensor_report_emails_error) {
              setStatus({ ...status, sensor_report_emails_error: '' })
            }
          }

          return (
            <div className="form-group">
              <label>
                Sensor Report Emails
                <Tooltip title="Email addresses to receive daily sensor reports. Press Enter or comma to add each email." placement='top' arrow>
                  <i className="fa fa-info-circle ms-1" />
                </Tooltip>
              </label>

              <div className={`email-tags-container ${
                errorsPresent ? 'is-invalid' : ''
              }`} style={{
                borderRadius: '10px',
                padding: '0.5rem',
                backgroundColor: 'white',
                minHeight: '40px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.25rem',
                alignItems: 'center'
              }}>
                {emails.map((email, index) => (
                  <span
                    key={index}
                    style={{
                      backgroundColor: '#6E757C',
                      color: 'white',
                      padding: '0.25rem 0.6rem',
                      borderRadius: '1rem',
                      fontSize: '0.875rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.30rem'
                    }}
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => removeEmail(email)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'white',
                        cursor: 'pointer',
                        padding: '0',
                        fontSize: '1rem',
                        lineHeight: '1'
                      }}
                      title={`Remove ${email}`}
                    >
                      ×
                    </button>
                  </span>
                ))}

                <input
                  type="text"
                  autoComplete="off"
                  value={emailInput}
                  onChange={handleInputChange}
                  onKeyDown={addEmail}
                  onBlur={addEmail}
                  placeholder={emails.length === 0 ? "Enter email addresses..." : "Add another email..."}
                  style={{
                    border: 'none',
                    outline: 'none',
                    backgroundColor: 'transparent',
                    flex: '1',
                    minWidth: '150px',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              {errorsPresent && (
                <div className="invalid-feedback" style={{ display: 'block' }}>
                  {errors['sensor_report_emails'] || (status && status['sensor_report_emails_error'])}
                </div>
              )}
            </div>
          )
        }

        const renderPhoneNumbersField = () => {
          const [phoneInput, setPhoneInput] = useState('')
          const phoneNumbers = values['critical_issue_phone_numbers'] || []
          const errorsPresent =
            (errors['critical_issue_phone_numbers'] && touched['critical_issue_phone_numbers']) ||
            (status && status['critical_issue_phone_numbers_error'])

          const isValidPhoneNumber = (phoneNumber) => {
            return /^\+?[1-9]\d{1,14}$/.test(phoneNumber)
          }

          const addPhoneNumber = (e) => {
            if (e.key === 'Enter' || e.key === ',' || e.type === 'blur') {
              e.preventDefault()
              const phoneNumber = phoneInput.trim()

              if (phoneNumber && isValidPhoneNumber(phoneNumber) && !phoneNumbers.includes(phoneNumber)) {
                const updatedPhoneNumbers = [...phoneNumbers, phoneNumber]
                setFieldValue('critical_issue_phone_numbers', updatedPhoneNumbers)
                setPhoneInput('')
              } else if (phoneNumber && !isValidPhoneNumber(phoneNumber)) {
                setStatus({
                  ...status,
                  critical_issue_phone_numbers_error: 'Please enter a valid phone number (e.g. +37120000000)'
                })
              }
            }
          }

          const removePhoneNumber = (phoneNumberToRemove) => {
            const updatedPhoneNumbers = phoneNumbers.filter(phoneNumber => phoneNumber !== phoneNumberToRemove)
            setFieldValue('critical_issue_phone_numbers', updatedPhoneNumbers)
          }

          const handleInputChange = (e) => {
            setPhoneInput(e.target.value)
            if (status?.critical_issue_phone_numbers_error) {
              setStatus({ ...status, critical_issue_phone_numbers_error: '' })
            }
          }

          return (
            <div className="form-group">
              <label>
                Critical Issue Receiver Phone Numbers
                <Tooltip title="Phone numbers to receive SMS alerts about critical robot issues. Press Enter or comma to add each number." placement='top' arrow>
                  <i className="fa fa-info-circle ms-1" />
                </Tooltip>
              </label>

              <div className={`email-tags-container ${
                errorsPresent ? 'is-invalid' : ''
              }`} style={{
                borderRadius: '10px',
                padding: '0.5rem',
                backgroundColor: 'white',
                minHeight: '40px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.25rem',
                alignItems: 'center'
              }}>
                {phoneNumbers.map((phoneNumber, index) => (
                  <span
                    key={index}
                    style={{
                      backgroundColor: '#6E757C',
                      color: 'white',
                      padding: '0.25rem 0.6rem',
                      borderRadius: '1rem',
                      fontSize: '0.875rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.30rem'
                    }}
                  >
                    {phoneNumber}
                    <button
                      type="button"
                      onClick={() => removePhoneNumber(phoneNumber)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'white',
                        cursor: 'pointer',
                        padding: '0',
                        fontSize: '1rem',
                        lineHeight: '1'
                      }}
                      title={`Remove ${phoneNumber}`}
                    >
                      ×
                    </button>
                  </span>
                ))}

                <input
                  type="text"
                  autoComplete="off"
                  value={phoneInput}
                  onChange={handleInputChange}
                  onKeyDown={addPhoneNumber}
                  onBlur={addPhoneNumber}
                  placeholder={phoneNumbers.length === 0 ? "Enter phone numbers..." : "Add another phone number..."}
                  style={{
                    border: 'none',
                    outline: 'none',
                    backgroundColor: 'transparent',
                    flex: '1',
                    minWidth: '150px',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              {errorsPresent && (
                <div className="invalid-feedback" style={{ display: 'block' }}>
                  {errors['critical_issue_phone_numbers'] || (status && status['critical_issue_phone_numbers_error'])}
                </div>
              )}
            </div>
          )
        }

        const renderSelect = (label, name, placeholder, selectOptions) => (
          <div className="form-group">
            <label>{label}</label>
            <Select
              classNamePrefix="react-select"
              className={`react-select inverted ${
                touched[name] && errors[name] && 'is-invalid'
              }`}
              options={selectOptions}
              placeholder={placeholder}
              id={name}
              name={name}
              onBlur={handleBlur}
              value={find(selectOptions, (opt) => opt.value === values[name])}
              onChange={(e) =>
                enhancedHandleChange({
                  target: { name: name, value: e.value }
                })
              }
            />
            {errors[name] && touched[name] && (
              <div className="invalid-feedback">{errors[name]}</div>
            )}
          </div>
        )

        const renderCheckbox = (label, name) => {
          const errorsPresent =
            (errors[name] && touched[name]) || (status && status[`${name}_error`])
          return (
            <fieldset className="form-group boolean">
              <div className="form-check">
                <input
                  id={name}
                  name={name}
                  type="checkbox"
                  checked={values[name]}
                  onChange={enhancedHandleChange}
                  className={`form-check-input boolean ${
                    errorsPresent && 'is-invalid'
                  }`}
                />
                <label className="form-check-label boolean" htmlFor={name}>
                  {label}
                </label>
                {errorsPresent && (
                  <div className="invalid-feedback">
                    {errors[name] || (status && status[`${name}_error`])}
                  </div>
                )}
              </div>
            </fieldset>
          )
        }

        const renderProviderOptionsSelectedValue = (selected) => {
          if (selected.length > 0) {
            return selected.join(', ')
          } else {
            return t('order_providers.select_order_providers')
          }
        }

        const renderProviderOptions = () => {
          if(props.orderProviderOptions.length == 0) {
            return null
          }

          const handleProviderChange = (event) => {
            const selectedValues = event.target.value;

            const updatedProviders = props.orderProviderOptions.filter(provider => provider != 'clear')
              .map((providerType) => {
              const existingProvider = selectedOrderProviders.find(
                (provider) => provider.provider_type === providerType.provider_type
              );

              if (selectedValues.includes(providerType.provider_type)) {
                if (existingProvider) {
                  return { ...existingProvider, _destroy: false };
                } else {
                  return {
                    id: null,
                    order_provider_id: providerType.id,
                    provider_type: providerType.provider_type,
                    credentials: {},
                    destroyable: true,
                    _destroy: false,
                  };
                }
              } else {
                if (existingProvider) {
                  return { ...existingProvider, _destroy: true };
                }
              }

              return null;
            }).filter(Boolean);

            setSelectedOrderProviders(updatedProviders);
          };

          return (
            <>
              <h4 className="mt-5">Order providers</h4>
              <div className="form-group">
                <MuiSelect
                  className="mt-3"
                  value={selectedOrderProvidersValue()}
                  onChange={handleProviderChange}
                  disabled={
                    selectedOrderProviders.some(
                      (provider) => provider.destroyable === false)
                  }
                  displayEmpty={true}
                  input={<OutlinedInput />}
                  renderValue={renderProviderOptionsSelectedValue}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                        width: 250,
                      },
                    },
                  }}
                  sx={{
                    width: '250px',
                    height: '40px',
                    backgroundColor: 'white',
                    color: selectedOrderProviders.length > 0 ? 'black' : '#757575',
                  }}
                >
                  {props.orderProviderOptions.map((option) => (
                    <MenuItem
                      key={option.provider_type}
                      value={option.provider_type}
                      >
                      <ListItemText primary={option.provider_type} />
                    </MenuItem>
                  ))}
                </MuiSelect>
              </div>
            </>
          )
        }

        const selectedOrderProvidersValue = () => {
          const value = selectedOrderProviders
            .filter((provider) => !provider._destroy)
            .map((provider) => provider.provider_type)

          return value.length == 0 ? '' : value
        }

        const updateSelectedOrderProviderFields = (orderProvider) => {
          setSelectedOrderProviders((prev) => {
            const updated = prev.map((provider) => {
              if (provider.provider_type === orderProvider.provider_type) {
                return orderProvider
              }
              return provider
            }
            )
            return updated
          }
        )
        }

        const toSelectOptions = (items) =>
          items.map((value) => ({ label: value, value: value }))

        return (
          <form autoComplete="off" onSubmit={handleSubmit}>
            <div className="row mb-3">
              <div className="col">
                {renderNameAndImageField()}
                {renderField(
                  t('address', { scope }),
                  'address',
                  'text',
                  t('enter_address', { scope })
                )}
                {renderSelect(
                  t('currency', { scope }),
                  'currency_id',
                  t('select_currency', { scope }),
                  props.currencies.map((currency) => ({
                    label: currency.code,
                    value: currency.id
                  }))
                )}
                {renderSelect(
                  t('temperature_scale', { scope }),
                  'temperature_scale',
                  t('select_temperature_scale', { scope }),
                  toSelectOptions(['celsius', 'fahrenheit'])
                )}
                {renderSelect(
                  t('weight_scale', { scope }),
                  'weight_scale',
                  t('select_weight_scale', { scope }),
                  toSelectOptions(['metric', 'imperial'])
                )}
                {(() => {
                  const previewTz = props.timezoneMappings?.[values.timezone] || values.timezone || 'UTC'
                  const previewNow = moment.tz(previewTz)
                  return (
                    <>
                      {renderSelect(
                        t('date_format', { scope }),
                        'date_format',
                        t('select_date_format', { scope }),
                        props.dateFormats.map((format) => ({
                          label: `${format} (${previewNow.format(format)})`,
                          value: format
                        }))
                      )}
                      {renderSelect(
                        t('time_format', { scope }),
                        'time_format',
                        t('select_time_format', { scope }),
                        props.timeFormats.map((format) => ({
                          label: `${format} (${previewNow.format(format)})`,
                          value: format
                        }))
                      )}
                    </>
                  )
                })()}
                {renderSelect(
                  t('timezone', { scope }),
                  'timezone',
                  t('select_timezone', { scope }),
                  (props.timezones || []).map((tz) => ({
                    label: tz,
                    value: tz
                  }))
                )}
                {renderField(
                  t('webhook_url', { scope }),
                  'webhook_url',
                  'text',
                  t('enter_webhook_url', { scope })
                )}
                {renderCheckbox(
                  t('ai_platform_enabled', { scope }),
                  'ai_platform_enabled',
                  'checkbox'
                )}
                {values.ai_platform_enabled == true && (
                  renderField(
                    t('ai_platform_workspace_id', { scope }),
                    'ai_platform_workspace_id',
                    'password',
                    t('enter_ai_platform_workspace_id', { scope })
                  )
                )}
                {renderRobotConnectionUrlField()}
                {renderField(
                  t('robot_connection_username', { scope }),
                  'robot_connection_username',
                  'text',
                  t('enter_robot_connection_username', { scope })
                )}
                {renderField(
                  t('robot_connection_password', { scope }),
                  'robot_connection_password',
                  'text',
                  t('enter_robot_connection_password', { scope }),
                  'off',
                  true
                )}
                {renderTextAreaField(
                  t('robot_certificate', { scope }),
                  'robot_certificate',
                  t('enter_robot_certificate', { scope }),
                  'off',
                  true // isSecure
                )}
                {renderTextAreaField(
                  t('robot_private_key', { scope }),
                  'robot_private_key',
                  t('enter_robot_private_key', { scope }),
                  'off',
                  true // isSecure
                )}
                {renderSensorEmailsField()}
                {renderPhoneNumbersField()}
                {renderField(
                  t('sensor_connection_url', { scope }),
                  'sensor_connection_url',
                  'text',
                  t('enter_ensor_connection_url', { scope })
                )}
                {renderCheckbox(
                  t('nonstop_working_hours', { scope }),
                  'nonstop_working_hours',
                  'checkbox'
                )}
                {values.nonstop_working_hours == true ? null : (
                  <>
                    {renderField(
                      t('working_start_hour', { scope }),
                      'working_start_hour',
                      'number',
                      t('enter_working_start_hour', { scope })
                    )}
                    {renderField(
                      t('working_end_hour', { scope }),
                      'working_end_hour',
                      'number',
                      t('enter_working_end_hour', { scope })
                    )}
                  </>
                )}
              </div>
            </div>
            {renderProviderOptions()}

            {!isEmpty(orderProviderLocationFields) && selectedOrderProviders.filter(
              (provider) => !provider._destroy).map((provider) =>
                <OrderProviderLocationForm
                  orderProvider={provider}
                  key={provider.provider_type}
                  requiredFields={orderProviderLocationFields[provider.provider_type] || []}
                  onSubmit={updateSelectedOrderProviderFields}
                  setIsSubmitted={(submitted) => setOrderProviderSubmitted(provider.provider_type, submitted)}
                  isSubmitted={orderProvidersSubmitted.find(
                    (p) => p.provider_type === provider.provider_type
                  )?.isSubmitted || false}
                  onCancel={() => {}}
                />
            )}

            <KitchenConfiguration
              kitchenConfiguration={props.kitchenConfiguration}
              setKitchenConfiguration={props.setKitchenConfiguration}
              kitchenConfigurationOptions={props.kitchenConfigurationOptions}
              kitchenConfigurationErrors={props.kitchenConfigurationErrors}
              setKitchenConfigurationErrors={props.setKitchenConfigurationErrors}
            />

            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting || configErrorsPresent() || !allOrderProvidersSubmitted()}
            >
              { props.action === 'create' ? t('create', { scope }) : t('update', { scope }) }
            </button>
            <button
              className="btn btn-link"
              onClick={() => window.history.back()}
            >
              {t('back', { scope })}
            </button>
          </form>
        )
      }}
    </Formik>
  )
}

export default LocationForm
